import React, { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import './EditContextModal.scss';

interface EditContextModalProps {
  isOpen: boolean;
  keywords: string[];
  onChange: (nextKeywords: string[]) => void;
  onFinish?: () => void;
  onClose: () => void;
}

export const EditContextModal: React.FC<EditContextModalProps> = ({
  isOpen,
  keywords,
  onChange,
  onFinish,
  onClose,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  if (!isOpen) {
    return null;
  }

  const addKeyword = () => {
    const normalizedKeyword = inputValue.trim();

    if (!normalizedKeyword) {
      return;
    }

    const hasKeywordAlready = keywords.some((keyword) => keyword.toLowerCase() === normalizedKeyword.toLowerCase());

    if (hasKeywordAlready) {
      setInputValue('');
      return;
    }

    onChange([...keywords, normalizedKeyword]);
    setInputValue('');
  };

  const removeKeyword = (keywordToRemove: string) => {
    onChange(keywords.filter((keyword) => keyword !== keywordToRemove));
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addKeyword();
      return;
    }

    if (event.key === 'Backspace' && !inputValue && keywords.length > 0) {
      onChange(keywords.slice(0, -1));
    }
  };

  const handleClose = () => {
    setInputValue('');
    onClose();
  };

  const handleFinish = () => {
    setInputValue('');
    onFinish?.();
    onClose();
  };

  return (
    <div className="new-setup__modal-overlay">
      <dialog open className="new-setup__context-modal" aria-labelledby="new-setup-context-modal-title">
        <header className="new-setup__context-modal-header">
          <button
            type="button"
            className="new-setup__context-modal-close"
            onClick={handleClose}
            aria-label={t('newSetup.contextModal.close')}
          >
            <BdsIcon theme="outline" name="close" size="medium" aria-hidden="true" />
          </button>

          <h2 id="new-setup-context-modal-title" className="new-setup__context-modal-title">
            {t('newSetup.contextModal.title')}
          </h2>
          <p className="new-setup__context-modal-subtitle">{t('newSetup.contextModal.subtitle')}</p>
        </header>

        <div className="new-setup__context-modal-content">
          <div className="new-setup__context-modal-divider" />

          <div className="new-setup__context-modal-copy">
            <h3 className="new-setup__context-modal-copy-title">{t('newSetup.contextModal.keywordsTitle')}</h3>
            <p className="new-setup__context-modal-copy-text">{t('newSetup.contextModal.keywordsDescription')}</p>
          </div>

          <section className="new-setup__context-editor" aria-label={t('newSetup.contextModal.editorAria')}>
            <header className="new-setup__context-editor-header">
              <div className="new-setup__context-editor-title-wrap">
                <h4 className="new-setup__context-editor-title">{t('newSetup.generated.context')}</h4>
                <span className="new-setup__context-editor-count">{keywords.length} kw</span>
              </div>
              <span className="new-setup__context-editor-collapse" aria-hidden="true">
                <BdsIcon theme="outline" name="arrow-up" size="medium" aria-hidden="true" />
              </span>
            </header>

            <p className="new-setup__context-editor-keywords-count">
              {t('newSetup.contextModal.keywordsCountLabel', { count: keywords.length })}
            </p>

            <div className="new-setup__context-editor-input-wrap">
              <div className="new-setup__context-editor-chip-grid">
                {keywords.map((keyword) => (
                  <span key={keyword} className="new-setup__context-editor-chip">
                    <span className="new-setup__context-editor-chip-label">{keyword}</span>
                    <button
                      type="button"
                      className="new-setup__context-editor-chip-remove"
                      onClick={() => removeKeyword(keyword)}
                      aria-label={t('newSetup.contextModal.removeKeywordAria', { keyword })}
                    >
                      <span className="new-setup__context-editor-chip-remove-icon" aria-hidden="true" />
                    </button>
                  </span>
                ))}

                <input
                  className="new-setup__context-editor-input"
                  value={inputValue}
                  placeholder={t('newSetup.contextModal.addKeywordPlaceholder')}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
            </div>
          </section>
        </div>

        <footer className="new-setup__context-modal-footer">
          <button type="button" className="new-setup__context-modal-confirm" onClick={handleFinish}>
            {t('newSetup.contextModal.finish')}
          </button>
        </footer>
      </dialog>
    </div>
  );
};
